import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createDoorVisitCoreApi,
  isNonBlankDoorVisitCoreString,
  type DoorVisitCoreApi,
  type DoorVisitCoreFetchImpl,
  type DoorVisitCoreTranscriptionEvent,
} from './doorVisitCoreApi';

export const DOOR_VISIT_TRANSCRIPT_POLL_INTERVAL_MS = 5_000;
export const DOOR_VISIT_TRANSCRIPT_POLL_DEADLINE_MS = 5 * 60_000;

export type DoorVisitTranscriptScheduler = {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

const defaultScheduler: DoorVisitTranscriptScheduler = {
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: timer => clearTimeout(timer),
};

export type DoorVisitTranscriptDetail = {
  doorVisitId: number;
  coreLeadId?: string | null;
  coreCallId?: string | null;
};

export type DoorVisitTranscriptEvent = {
  speaker: 'Home Owner' | 'Canvasser' | 'Unknown Speaker';
  occurredAt: unknown;
  transcript: string;
};

export type DoorVisitTranscriptState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  events: DoorVisitTranscriptEvent[];
  refreshing: boolean;
};

export type UseDoorVisitTranscriptOptions = {
  routeDoorVisitId: number;
  detail: DoorVisitTranscriptDetail | null;
  fetchImpl?: DoorVisitCoreFetchImpl;
  api?: Pick<DoorVisitCoreApi, 'getTranscript'>;
  scheduler?: DoorVisitTranscriptScheduler;
};

export type UseDoorVisitTranscriptResult = {
  state: DoorVisitTranscriptState;
  retry: () => void;
};

const getTranscriptSpeaker = (speaker: string): DoorVisitTranscriptEvent['speaker'] => {
  switch (speaker.trim().toLowerCase()) {
    case 'customer':
      return 'Home Owner';
    case 'agent':
    case 'assistant':
    case 'system':
      return 'Canvasser';
    default:
      return 'Unknown Speaker';
  }
};

const toTimestamp = (occurredAt: unknown): number | null => {
  let date: Date;

  if (typeof occurredAt === 'string') {
    date = new Date(occurredAt);
  } else if (typeof occurredAt === 'number') {
    date = new Date(occurredAt);
  } else if (occurredAt instanceof Date) {
    date = occurredAt;
  } else {
    return null;
  }

  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const formatDoorVisitTranscriptTimestamp = (occurredAt: unknown): string => {
  const timestamp = toTimestamp(occurredAt);
  return timestamp === null ? 'Timestamp unavailable' : new Date(timestamp).toLocaleString();
};

export const normalizeDoorVisitTranscriptEvents = (
  events: readonly DoorVisitCoreTranscriptionEvent[],
): DoorVisitTranscriptEvent[] => {
  return events
    .map((event, index) => ({
      index,
      timestamp: toTimestamp(event.occurredAt),
      event: {
        speaker: getTranscriptSpeaker(event.speaker),
        occurredAt: event.occurredAt,
        transcript: event.transcript,
      },
    }))
    .sort((left, right) => {
      if (left.timestamp === null && right.timestamp === null) return left.index - right.index;
      if (left.timestamp === null) return 1;
      if (right.timestamp === null) return -1;

      return left.timestamp === right.timestamp ? left.index - right.index : left.timestamp - right.timestamp;
    })
    .map(({ event }) => event);
};

const idleState = (): DoorVisitTranscriptState => ({ status: 'idle', events: [], refreshing: false });

const loadingState = (): DoorVisitTranscriptState => ({ status: 'loading', events: [], refreshing: false });

const readyState = (events: DoorVisitTranscriptEvent[], refreshing = false): DoorVisitTranscriptState => ({
  status: 'ready',
  events,
  refreshing,
});

const errorState = (): DoorVisitTranscriptState => ({ status: 'error', events: [], refreshing: false });

export const useDoorVisitTranscript = ({
  routeDoorVisitId,
  detail,
  fetchImpl,
  api: apiOverride,
  scheduler = defaultScheduler,
}: UseDoorVisitTranscriptOptions): UseDoorVisitTranscriptResult => {
  const api = useMemo(
    () => apiOverride ?? createDoorVisitCoreApi({ fetchImpl }),
    [apiOverride, fetchImpl],
  );
  const [state, setState] = useState<DoorVisitTranscriptState>(idleState);
  const retryRequestRef = useRef<() => void>(() => undefined);

  const retry = useCallback((): void => retryRequestRef.current(), []);
  const coreLeadId =
    detail !== null && detail.doorVisitId === routeDoorVisitId && isNonBlankDoorVisitCoreString(detail.coreLeadId)
      ? detail.coreLeadId
      : null;
  const coreCallId =
    detail !== null && detail.doorVisitId === routeDoorVisitId && isNonBlankDoorVisitCoreString(detail.coreCallId)
      ? detail.coreCallId
      : null;

  useEffect(() => {
    if (coreLeadId === null || coreCallId === null) {
      setState(idleState());
      retryRequestRef.current = () => undefined;
      return undefined;
    }

    let active = true;
    let inFlight = false;
    let automaticPollingEnabled = true;
    let phase: DoorVisitTranscriptState['status'] = 'loading';
    let currentEvents: DoorVisitTranscriptEvent[] = [];
    let requestGeneration = 0;
    let requestController: AbortController | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
    const deadline = scheduler.now() + DOOR_VISIT_TRANSCRIPT_POLL_DEADLINE_MS;

    const clearPollTimer = (): void => {
      if (pollTimer !== null) {
        scheduler.clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const clearDeadlineTimer = (): void => {
      if (deadlineTimer !== null) {
        scheduler.clearTimeout(deadlineTimer);
        deadlineTimer = null;
      }
    };

    const abortCurrentRequest = (): void => {
      requestGeneration += 1;
      const controller = requestController;
      requestController = null;
      inFlight = false;
      controller?.abort();
    };

    const stopAtDeadline = (): void => {
      automaticPollingEnabled = false;
      clearPollTimer();
      clearDeadlineTimer();
      abortCurrentRequest();

      if (active && phase !== 'error') {
        phase = 'ready';
        setState(readyState(currentEvents));
      }
    };

    const stopAutomatically = (): void => {
      automaticPollingEnabled = false;
      clearPollTimer();
      clearDeadlineTimer();
    };

    const schedulePoll = (): void => {
      if (!active || !automaticPollingEnabled || inFlight) return;

      const remainingMs = deadline - scheduler.now();
      if (remainingMs <= 0) {
        stopAtDeadline();
        return;
      }

      clearPollTimer();
      pollTimer = scheduler.setTimeout(() => {
        pollTimer = null;
        requestTranscript('automatic');
      }, Math.min(DOOR_VISIT_TRANSCRIPT_POLL_INTERVAL_MS, remainingMs));
    };

    const requestTranscript = (kind: 'initial' | 'automatic' | 'manual'): void => {
      if (!active || inFlight) return;

      const deadlineHasPassed = scheduler.now() >= deadline;
      if (kind !== 'manual' && deadlineHasPassed) {
        stopAtDeadline();
        return;
      }

      clearPollTimer();
      if (kind === 'manual') {
        automaticPollingEnabled = !deadlineHasPassed;
        if (deadlineHasPassed) clearDeadlineTimer();
      }

      inFlight = true;
      const controller = new AbortController();
      requestController = controller;
      const generation = ++requestGeneration;
      if (kind === 'automatic') {
        setState(readyState(currentEvents, true));
      } else {
        phase = 'loading';
        setState(loadingState());
      }

      let request: ReturnType<DoorVisitCoreApi['getTranscript']>;
      try {
        request = api.getTranscript({ leadId: coreLeadId, callId: coreCallId }, { signal: controller.signal });
      } catch {
        request = Promise.resolve({ kind: 'error' });
      }

      void request.then(result => {
        if (!active || controller.signal.aborted || generation !== requestGeneration) return;

        requestController = null;
        inFlight = false;

        if (result.kind === 'aborted') return;

        if (result.kind === 'error') {
          stopAutomatically();
          phase = 'error';
          setState(errorState());
          return;
        }

        if (result.kind === 'unavailable') {
          stopAutomatically();
          currentEvents = [];
          phase = 'ready';
          setState(readyState(currentEvents));
          return;
        }

        currentEvents = normalizeDoorVisitTranscriptEvents(result.events);
        phase = 'ready';
        setState(readyState(currentEvents));
        if (currentEvents.length > 0) {
          stopAutomatically();
          return;
        }

        schedulePoll();
      }).catch(() => {
        if (!active || controller.signal.aborted || generation !== requestGeneration) return;

        requestController = null;
        inFlight = false;
        stopAutomatically();
        phase = 'error';
        setState(errorState());
      });
    };

    const retryRequest = (): void => {
      requestTranscript('manual');
    };

    retryRequestRef.current = retryRequest;
    deadlineTimer = scheduler.setTimeout(stopAtDeadline, DOOR_VISIT_TRANSCRIPT_POLL_DEADLINE_MS);
    setState(loadingState());
    requestTranscript('initial');

    return () => {
      active = false;
      retryRequestRef.current = () => undefined;
      clearPollTimer();
      clearDeadlineTimer();
      abortCurrentRequest();
    };
  }, [api, coreCallId, coreLeadId, routeDoorVisitId, scheduler]);

  return { state, retry };
};
